'use client';

import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import type { ReactNode, RefObject } from 'react';
import type { PopoverPlacement } from './popover-position';
import { usePopoverStore } from './PopoverContext';

interface UsePopoverOptions {
    /** How long the pointer rests before the popover shows, in milliseconds. */
    delay?: number;
    placement?: PopoverPlacement;
    /** Whether the popover offers a pin button that keeps it open. */
    pinnable?: boolean;
}

interface UsePopoverResult<T extends HTMLElement> {
    /** Put on the element the popover describes and points at. */
    triggerRef: RefObject<T>;
    showPopover: (content: ReactNode) => void;
    /** Hides this trigger's popover, unless it is pinned. */
    hidePopover: () => void;
    /** Pins this trigger's popover open, if it is showing. */
    pinPopover: () => void;
    isPopoverVisible: boolean;
}

/**
 * Binds an element to the app's shared hover popover (`PopoverProvider`).
 * Every action acts only on this trigger's own popover.
 */
export function usePopover<T extends HTMLElement = HTMLDivElement>(options: UsePopoverOptions = {}): UsePopoverResult<T> {
    const { delay = 500, placement = 'top', pinnable = false } = options;
    const { show, hide, pin, release, getState, subscribe } = usePopoverStore();
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const triggerRef = useRef<T>(null);
    // The element this trigger last showed the popover for. React has already
    // cleared `triggerRef` by the time the unmount cleanup runs, so the
    // cleanup needs its own record of which popover is this trigger's.
    const shownForElementRef = useRef<HTMLElement | null>(null);

    const showFor = useCallback((content: ReactNode, triggerElement: HTMLElement): void => {
        shownForElementRef.current = triggerElement;
        show(content, triggerElement, placement, pinnable);
    }, [pinnable, placement, show]);

    const clearPendingShow = useCallback((): void => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    // On unmount, drop a pending show and take down the popover if this
    // trigger is the one showing it, so it can't outlive its trigger.
    useEffect(() => (): void => {
        clearPendingShow();
        if (shownForElementRef.current) {
            release(shownForElementRef.current);
        }
    }, [clearPendingShow, release]);

    // Subscribed as a boolean, so this trigger re-renders only when its own
    // popover shows or hides, not when another trigger's does.
    const isPopoverVisible = useSyncExternalStore(subscribe, () => {
        const state = getState();
        return state.isVisible && state.triggerElement === triggerRef.current;
    });

    const showPopover = useCallback((content: ReactNode): void => {
        if (!triggerRef.current) {
            return;
        }
        clearPendingShow();
        if (delay > 0 && !isPopoverVisible) {
            timeoutRef.current = setTimeout(() => {
                if (triggerRef.current) {
                    showFor(content, triggerRef.current);
                }
            }, delay);
            return;
        }
        showFor(content, triggerRef.current);
    }, [clearPendingShow, delay, isPopoverVisible, showFor]);

    const hidePopover = useCallback((): void => {
        clearPendingShow();
        if (shownForElementRef.current) {
            hide(shownForElementRef.current);
        }
    }, [clearPendingShow, hide]);

    const pinPopover = useCallback((): void => {
        if (shownForElementRef.current) {
            pin(shownForElementRef.current);
        }
    }, [pin]);

    return {
        triggerRef,
        showPopover,
        hidePopover,
        pinPopover,
        isPopoverVisible,
    };
}
